/** The trace as sentences (screens §2.4): each line is the trace's own number in words; nothing is recomputed beyond a sum or a sort. */
import { PREVIEW_GATED_KPIS, type AlmSnapshot, type KpiId, type PreviewTrace } from "@bsim/domain";
import { formatMetric } from "@gxs/metric-defs";
import { formatBp, formatUsdB, formatUsdM, formatYears } from "../../format";
import { kpiLabel } from "../../format/labels";
import { RATE_MOVE_PHRASE } from "@bsim/engine";
import { deltaText, deltaTone, PREVIEW_ROWS } from "../previewRows";

export type TraceDecision = PreviewTrace["decisions"][number];
type Setting = TraceDecision["settings"][number];

/** A product or book id as a last-resort label ("int_checking" → "Int checking"); the horizon's product list names it properly. */
export const plainId = (id: string): string => { const w = id.replace(/[._]/g, " "); return w.charAt(0).toUpperCase() + w.slice(1); };

const MINUS = "−";

/** "+$27M", "−$1.27B": a dollar change at the size a reader thinks in. */
export const usdChange = (usd: number): string => {
  if (Math.abs(usd) < 1e9) return formatUsdM(usd, { signed: true });
  return `${usd < 0 ? MINUS : "+"}${formatUsdB(usd)}`;
};

const KPI_ORDER: readonly KpiId[] = PREVIEW_ROWS.map((r) => r.kpi);
const isGatedKpi = (kpi: KpiId): boolean => (PREVIEW_GATED_KPIS as readonly KpiId[]).includes(kpi);

/** A decision's weight on the share price, the order of the cards (price is never masked). */
export const priceEffect = (l: { readonly kpis: PreviewTrace["interaction"]["kpis"] }): number => Math.abs(l.kpis.price ?? 0);

/** The KPIs this snapshot moves (in preview-row order), as "NIM −16bp"; gated ones are left out before the call and counted as `locked`. */
export function ratioChanges(kpis: PreviewTrace["interaction"]["kpis"], committed: boolean): { readonly parts: readonly { readonly kpi: KpiId; readonly text: string }[]; readonly locked: boolean } {
  const moved = KPI_ORDER.filter((kpi) => kpis[kpi] !== undefined && deltaTone(kpi, kpis[kpi] ?? 0) !== "flat");
  const open = moved.filter((kpi) => committed || !isGatedKpi(kpi));
  return { parts: open.map((kpi) => ({ kpi, text: `${kpiLabel(kpi)} ${deltaText(kpi, kpis[kpi] ?? 0)}${kpi === "price" ? " this year" : ""}` })), locked: open.length < moved.length };
}

const afterColon = (label: string): string => { const at = label.indexOf(": "); return at < 0 ? label : label.slice(at + 2); };

export const listOf = (names: readonly string[]): string => (names.length <= 1 ? (names[0] ?? "") : names.length <= 3 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : `${names.length} products`);

const value = (v: number | string, unit: Setting["unit"]): string =>
  typeof v === "string" ? v : unit === "rate" ? formatMetric(v, "rate", { precision: 0 }) : unit === "usd" ? formatUsdM(v) : String(v);

/** "+25bp rate paid against the reference rate on interest checking and money market ($10.9B)". */
export function settingText(s: Setting, productName: (id: string) => string): string {
  const name = afterColon(s.label);
  const where = s.scope.length === 0 ? "" : ` on ${listOf(s.scope.map(productName))}`;
  const base = s.baseUsd === undefined ? "" : ` (${formatUsdB(s.baseUsd)})`;
  if (s.unit === "bp" && typeof s.from === "number" && typeof s.to === "number") return `${formatBp(s.to - s.from, { signed: true })} ${name}${where}${base}`;
  return `${name}${where}${base}: ${value(s.from, s.unit)} → ${value(s.to, s.unit)}`;
}

/** The `n` largest by size, biggest first (ties keep their order). */
export const largest = <T>(items: readonly T[], size: (t: T) => number, n: number): T[] => [...items].sort((a, b) => Math.abs(size(b)) - Math.abs(size(a))).slice(0, n);

/** A decision whose card is worth showing: it wrote a setting, or something moved. */
export const moved = (l: TraceDecision): boolean => l.settings.length > 0 || l.postings.length > 0 || l.lines.length > 0 || Object.keys(l.kpis).length > 0;

/** The draft's whole move on each KPI, the decisions one at a time, and what is left for how they interact: rows plus interaction are the draft's move exactly (preview-trace.ts). */
export function togetherOf(trace: PreviewTrace): { readonly total: PreviewTrace["interaction"]["kpis"]; readonly oneByOne: PreviewTrace["interaction"]["kpis"]; readonly interaction: PreviewTrace["interaction"]["kpis"] } {
  const one: Partial<Record<KpiId, number>> = {}, all: Partial<Record<KpiId, number>> = {};
  for (const kpi of KPI_ORDER) {
    const s = trace.decisions.reduce((t, l) => t + (l.kpis[kpi] ?? 0), 0);
    if (trace.decisions.some((l) => l.kpis[kpi] !== undefined)) one[kpi] = s;
    if (trace.decisions.some((l) => l.kpis[kpi] !== undefined) || trace.interaction.kpis[kpi] !== undefined) all[kpi] = s + (trace.interaction.kpis[kpi] ?? 0);
  }
  return { total: all, oneByOne: one, interaction: trace.interaction.kpis };
}

/** A posting step whose name says which way it went: the engine's "Volume growth" is a decline when the line fell, so the reason follows the sign. */
const SIGNED_STEP: Readonly<Record<string, readonly [up: string, down: string]>> = { "volume growth": ["higher volume", "lower volume"] };

/** "Money market deposits fall $85.5M (lower volume)": the verb and the reason follow the sign, so a decline is never labelled growth. */
export function postingText(p: { readonly step: string; readonly lineId: string; readonly deltaUsd: number }): string {
  const size = usdChange(Math.abs(p.deltaUsd)).replace(/^\+/, "");
  const step = p.step.toLowerCase();
  const reason = SIGNED_STEP[step]?.[p.deltaUsd < 0 ? 1 : 0] ?? step;
  return `${p.lineId} ${p.deltaUsd < 0 ? "fall" : "rise"} ${size} (${reason})`;
}

/** The rate-position decision moves equity value through its own swaps (its setting line says so); every other decision that moves it does so through the books it grows or shrinks. */
const OWN_SWAP_DECISION = "treasury.ratePosition";

/** What the trace shows of a decision that moves equity value through a book: the line it moved and by how much. No mechanism is claimed; the engine's own reason, when it has one, prints beside it. Null when equity value does not move, no line moved, or the decision's own swap is the cause. */
export function eveBookLine(l: TraceDecision, book: { readonly lineId: string; readonly deltaUsd: number } | undefined): string | null {
  const move = l.kpis.eve200;
  if (move === undefined || book === undefined || deltaTone("eve200", move) === "flat" || l.decisionId.startsWith(OWN_SWAP_DECISION)) return null;
  return `This change moves ${book.lineId} ${usdChange(book.deltaUsd)} by year-end; equity value ${RATE_MOVE_PHRASE.worse} moves ${deltaText("eve200", move)} with it.`;
}

/** Equity duration and the cost of a 2-point rise, round start to year-end on your plan (the year's market and balance-sheet drift are in it, as on the ALM tab). Null when neither moved at the printed size. */
export function durationLine(start: AlmSnapshot, draft: AlmSnapshot): string | null {
  const years = (a: number): string => formatYears(a);
  const [from, to] = [start.eve.plus200.deltaUsd, draft.eve.plus200.deltaUsd];
  const usd = (x: number): string => `${x < 0 ? "costs" : "gains"} ${formatMetric(Math.abs(x), "usd")}`;
  if (years(start.durationOfEquityYears) === years(draft.durationOfEquityYears) && usd(from) === usd(to)) return null;
  return `Your plan takes equity duration from ${years(start.durationOfEquityYears)} at the round start to ${years(draft.durationOfEquityYears)} at year-end (round start to year-end on your plan), so a 2-point rise in rates ${usd(from)} at the round start and ${usd(to).replace(/^(costs|gains) /, (m) => (usd(from).startsWith(m) ? "" : m))} at year-end.`;
}
